import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import VetoTimeline from './VetoTimeline';
import VetoResults from './VetoResults';
import './VetoFlow.css';

const FALLBACK_MAP_INFO = {
  de_mirage: { name: 'Mirage', category: 'Active Duty' },
  de_inferno: { name: 'Inferno', category: 'Active Duty' },
  de_nuke: { name: 'Nuke', category: 'Active Duty' },
  de_overpass: { name: 'Overpass', category: 'Active Duty' },
  de_ancient: { name: 'Ancient', category: 'Active Duty' },
  de_anubis: { name: 'Anubis', category: 'Active Duty' },
  de_vertigo: { name: 'Vertigo', category: 'Active Duty' },
};

function getMapName(tag, mapPoolInfo) {
  if (mapPoolInfo?.[tag]?.name) return mapPoolInfo[tag].name;
  if (FALLBACK_MAP_INFO[tag]?.name) return FALLBACK_MAP_INFO[tag].name;
  if (tag.startsWith('de_')) {
    const base = tag.replace('de_', '');
    return base.charAt(0).toUpperCase() + base.slice(1);
  }
  return tag;
}

function generateRounds(matchType, bo1BanFinish = false) {
  if (matchType === 'BO1') {
    if (bo1BanFinish) {
      // Ban hela vägen ner till 1 karta — sista action är ban, inte pick.
      return [
        { type: 'ban', team: 'team1', count: 2 },
        { type: 'ban', team: 'team2', count: 3 },
        { type: 'ban', team: 'team1', count: 1 },
        { type: 'side', team: 'team2', count: 1 },
      ];
    }
    return [
      { type: 'ban', team: 'team1', count: 2 },
      { type: 'ban', team: 'team2', count: 3 },
      { type: 'pick', team: 'team1', count: 1 },
      { type: 'side', team: 'team2', count: 1 },
    ];
  } else if (matchType === 'BO3') {
    return [
      { type: 'ban', team: 'team1', count: 2 },
      { type: 'ban', team: 'team2', count: 2 },
      { type: 'pick', team: 'team1', count: 1 },
      { type: 'pick', team: 'team2', count: 1 },
      { type: 'side', team: 'team2', count: 1 },
      { type: 'side', team: 'team1', count: 1 },
    ];
  } else {
    return [
      { type: 'ban', team: 'team1', count: 1 },
      { type: 'ban', team: 'team2', count: 1 },
      { type: 'pick', team: 'team1', count: 1 },
      { type: 'pick', team: 'team2', count: 1 },
      { type: 'pick', team: 'team1', count: 1 },
      { type: 'pick', team: 'team2', count: 1 },
      { type: 'side', team: 'team2', count: 1 },
      { type: 'side', team: 'team1', count: 1 },
      { type: 'side', team: 'team2', count: 1 },
      { type: 'side', team: 'team1', count: 1 },
    ];
  }
}

function buildInitialState(matchType, vetoMode, pool, knifeRound, mapInfoByTag, bo1BanFinish) {
  const roundDefs = generateRounds(matchType, bo1BanFinish);
  const rounds = roundDefs.map((r, i) => ({
    round_num: i,
    type: r.type,
    team: r.team,
    count: r.count,
    status: i === 0 ? 'active' : 'pending',
    votes: i === 0 ? { _count: 0 } : undefined,
    result: undefined,
    timer_start: i === 0 ? Date.now() / 1000 : undefined,
    timer_duration: 30,
  }));

  const mapsRemaining = Array.isArray(pool) && pool.length > 0 ? [...pool] : ['de_mirage'];
  const mapPoolInfo = {};
  for (const tag of mapsRemaining) {
    mapPoolInfo[tag] = mapInfoByTag?.[tag] || FALLBACK_MAP_INFO[tag] || { name: getMapName(tag), category: 'Custom' };
  }

  return {
    veto_mode: vetoMode,
    match_type: matchType,
    current_round: 0,
    maps_remaining: mapsRemaining,
    banned_maps: [],
    picked_maps: [],
    map_pool_info: mapPoolInfo,
    knife_round: knifeRound,
    team1_players: ['1', '2', '3', '4', '5'],
    team2_players: ['6', '7', '8', '9', '10'],
    rounds,
  };
}

const TIMER_DURATION = 8;

/**
 * Pure-frontend simulation of the veto flow. Drives timer, auto-submit,
 * ban/pick/side resolution, and renders the same UI components used by the
 * real VetoFlow. Shared between VetoDemo (veto-only) and CreateMatchDemo
 * (full match-creation demo).
 *
 * Pass a new `resetKey` to force a full state reset (e.g. when the user
 * clicks "Starta om" in the parent).
 */
export default function VetoSimulator({
  matchType,
  vetoMode,
  pool,
  knifeRound = true,
  mapInfoByTag,
  resetKey = 0,
  onComplete,
  bo1BanFinish = false,
}) {
  const { t } = useTranslation();
  const [vetoState, setVetoState] = useState(() =>
    buildInitialState(matchType, vetoMode, pool, knifeRound, mapInfoByTag, bo1BanFinish)
  );
  const [selectedMaps, setSelectedMaps] = useState([]);
  const [selectedSide, setSelectedSide] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(TIMER_DURATION);

  const currentRound = vetoState.rounds[vetoState.current_round];
  const roundType = currentRound?.type;
  const roundTeam = currentRound?.team;
  const roundCount = currentRound?.count || 1;
  const isComplete = vetoState.phase === 'complete';

  useEffect(() => {
    setVetoState(buildInitialState(matchType, vetoMode, pool, knifeRound, mapInfoByTag, bo1BanFinish));
    setSelectedMaps([]);
    setSelectedSide(null);
    setSubmitted(false);
    setTimeLeft(TIMER_DURATION);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    if (isComplete || !currentRound?.timer_start) return;
    const endTime = currentRound.timer_start + TIMER_DURATION;
    const interval = setInterval(() => {
      const left = Math.max(0, Math.ceil(endTime - Date.now() / 1000));
      setTimeLeft(left);
    }, 250);
    return () => clearInterval(interval);
  }, [currentRound?.timer_start, isComplete]);

  useEffect(() => {
    if (isComplete || submitted || !currentRound?.timer_start) return;
    const endTime = currentRound.timer_start + TIMER_DURATION;
    const msLeft = (endTime * 1000) - Date.now();
    if (msLeft <= 0) return;

    const timer = setTimeout(() => {
      handleSubmit(true);
    }, msLeft);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRound?.timer_start, isComplete, submitted]);

  useEffect(() => {
    if (isComplete && onComplete) {
      onComplete(vetoState);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  const toggleMap = (tag) => {
    if (submitted) return;
    setSelectedMaps(prev => {
      if (prev.includes(tag)) return prev.filter(m => m !== tag);
      if (roundType === 'pick' && prev.length >= 1) return [tag];
      if (roundType === 'ban' && prev.length >= roundCount) return prev;
      return [...prev, tag];
    });
  };

  const handleSubmit = useCallback(() => {
    setSubmitted(true);

    setTimeout(() => {
      setVetoState(prev => {
        const state = JSON.parse(JSON.stringify(prev));
        const round = state.rounds[state.current_round];
        if (!round) return prev;

        if (round.type === 'ban') {
          let toBan = [...selectedMaps];
          if (toBan.length < round.count) {
            const available = state.maps_remaining.filter(m => !toBan.includes(m));
            while (toBan.length < round.count && available.length > 0) {
              const idx = Math.floor(Math.random() * available.length);
              toBan.push(available.splice(idx, 1)[0]);
            }
          }
          round.status = 'complete';
          round.result = { banned_maps: toBan };
          state.maps_remaining = state.maps_remaining.filter(m => !toBan.includes(m));
          state.banned_maps.push(...toBan.map(m => ({ map: m, team: round.team })));
        } else if (round.type === 'pick') {
          let toPick = selectedMaps.length > 0
            ? selectedMaps[0]
            : state.maps_remaining[Math.floor(Math.random() * state.maps_remaining.length)];
          round.status = 'complete';
          round.result = { picked_maps: [toPick] };
          state.maps_remaining = state.maps_remaining.filter(m => m !== toPick);
          state.picked_maps.push({ map: toPick, team: round.team });
        } else if (round.type === 'side') {
          const side = selectedSide || (Math.random() > 0.5 ? 'ct' : 't');
          round.status = 'complete';
          round.result = { side };
          const opposingTeam = round.team === 'team1' ? 'team2' : 'team1';
          const pick = state.picked_maps.find(p => p.team === opposingTeam && !p.side);
          if (pick) pick.side = side;
        }

        const nextRoundIdx = state.current_round + 1;
        if (nextRoundIdx < state.rounds.length) {
          state.current_round = nextRoundIdx;
          state.rounds[nextRoundIdx].status = 'active';
          state.rounds[nextRoundIdx].votes = { _count: 0 };
          state.rounds[nextRoundIdx].timer_start = Date.now() / 1000;
          state.rounds[nextRoundIdx].timer_duration = TIMER_DURATION;
        } else {
          state.phase = 'complete';
        }

        return state;
      });

      setSelectedMaps([]);
      setSelectedSide(null);
      setSubmitted(false);
      setTimeLeft(TIMER_DURATION);
    }, 800);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaps, selectedSide]);

  const teamLabel = roundTeam === 'team1' ? 'Team 1' : 'Team 2';
  let roundTitle = '';
  let roundDesc = '';
  if (roundType === 'ban') {
    roundTitle = t('veto.simBan', { team: teamLabel });
    roundDesc = roundCount === 1
      ? t('veto.banInstructionOne', { count: roundCount })
      : t('veto.banInstructionMany', { count: roundCount });
  } else if (roundType === 'pick') {
    roundTitle = t('veto.simPick', { team: teamLabel });
    roundDesc = t('veto.pickMine');
  } else if (roundType === 'side') {
    roundTitle = t('veto.simSide', { team: teamLabel });
    roundDesc = t('veto.sideMine');
  }

  const voteCount = currentRound?.votes?._count ?? 0;
  const totalPlayers = 5;

  return (
    <>
      <div className="veto-mode-label">
        {vetoState.veto_mode === 'team_vote' ? t('veto.modeTeamVote') : t('veto.modeCaptainFull')}
        {' — '}
        {vetoState.match_type}
      </div>

      {vetoState.rounds.length > 0 && (
        <VetoTimeline rounds={vetoState.rounds} currentRound={vetoState.current_round} />
      )}

      {isComplete ? (
        <VetoResults vetoState={vetoState} onContinue={() => {}} />
      ) : (
        <div className="vote-round">
          <div className="round-header">
            <h2 className={`round-title ${roundTeam}`}>{roundTitle}</h2>
            <div className="round-timer">
              <div
                className="timer-bar"
                style={{ width: `${(timeLeft / TIMER_DURATION) * 100}%` }}
              />
              <span className="timer-text">{timeLeft}s</span>
            </div>
          </div>

          <p className="round-desc">{roundDesc}</p>

          <div className="vote-progress">
            <span>{submitted ? `${voteCount + 1}` : voteCount}/{totalPlayers} {t('veto.haveVoted')}</span>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${((submitted ? voteCount + 1 : voteCount) / totalPlayers) * 100}%` }}
              />
            </div>
          </div>

          {roundType === 'side' && (
            <div className="side-vote">
              <button
                className={`side-btn ct ${selectedSide === 'ct' ? 'selected' : ''}`}
                onClick={() => { if (!submitted) setSelectedSide('ct'); }}
              >
                <img className="side-img" src="/stats/images/maps/ct.jpg" alt="CT" />
                <span className="side-label">CT</span>
                <span className="side-desc">{t('veto.ctName')}</span>
              </button>
              <button
                className={`side-btn t ${selectedSide === 't' ? 'selected' : ''}`}
                onClick={() => { if (!submitted) setSelectedSide('t'); }}
              >
                <img className="side-img" src="/stats/images/maps/t.jpg" alt="T" />
                <span className="side-label">T</span>
                <span className="side-desc">{t('veto.tName')}</span>
              </button>
            </div>
          )}

          {roundType !== 'side' && (
            <div className="vote-map-grid">
              {vetoState.maps_remaining.map(tag => {
                const isSelected = selectedMaps.includes(tag);
                const imgExt = tag === 'de_train' ? 'png' : 'jpg';
                return (
                  <div
                    key={tag}
                    className={`vote-map-card ${isSelected ? (roundType === 'ban' ? 'ban-selected' : 'pick-selected') : ''} ${submitted ? 'disabled' : ''}`}
                    onClick={() => toggleMap(tag)}
                    style={{ backgroundImage: `url(/stats/images/maps/${tag}.${imgExt})` }}
                  >
                    <span className="vote-map-name">{getMapName(tag, vetoState.map_pool_info)}</span>
                    {isSelected && (
                      <span className="vote-map-icon">
                        {roundType === 'ban' ? '✕' : '✓'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {roundType !== 'side' && !submitted && (
            <div className="selection-counter">
              {selectedMaps.length}/{roundType === 'pick' ? 1 : roundCount} {t('veto.selectedAutoSend')}
            </div>
          )}

          {submitted && (
            <div className="vote-submitted">{t('veto.voteSubmitted')}</div>
          )}
        </div>
      )}
    </>
  );
}
